import {
  createElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Download,
  ExternalLink as ExternalLinkIcon,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Link2,
  Lock,
  Paperclip,
  Plus,
  Presentation,
  Trash2,
  Upload as UploadIcon,
  X,
} from 'lucide-react'
import { api, extractApiError, isPlanLimitError } from '../lib/api'
import { CONTAINS_EMOJI } from '../lib/validators'
import { ConfirmDialog } from './ui/ConfirmDialog'
import VisibilityPills from './VisibilityPills'
import { useSpaceAccess } from '../lib/useSpaceAccess'
import {
  UPLOAD_ACCEPT_ATTR,
  UPLOAD_EXTENSIONS_TEXT,
  isAllowedUploadFile,
} from '../lib/upload-extensions'
import { useMaxUploadBytes } from '../lib/useMaxUploadBytes'

/**
 * Per-task Files & Links column cell.
 *
 * Renders a small paperclip-button inside the List view's
 * `attachments` column. The button is the cell's only chrome by
 * default — a count badge appears when the task already has
 * attachments. Clicking the button opens a popover anchored to the
 * cell that:
 *
 *   1. Lists the current attachments (files + external links), each
 *      with a remove-button (X) restricted to the actor who added
 *      it OR a Company Admin (backend enforces the rule; the UI just
 *      shows the X for everyone and lets the backend reject).
 *   2. Exposes an **Upload** button that picks a file from disk,
 *      uploads it via `POST /files` (stamped with the parent
 *      Space's `spaceId` so it also surfaces in the Space's Files
 *      tab + tenant-wide library), then attaches it to the task
 *      via `POST /tasks/:taskId/attachments`.
 *   3. Exposes an **Add link** mini-form that prompts for title +
 *      URL, creates the link via `POST /external-links`, then
 *      attaches it via the same task-attachments endpoint.
 *
 * Both mutations invalidate the task-attachments query so the cell
 * count + popover list refresh immediately.
 *
 * Props:
 *   taskId   — required; identifies which task this cell belongs to
 *   spaceId  — required; piped through to file/link upload so the
 *              attachment also lands in the Space's Files library
 *
 * Why the two-step "create file then attach" flow: the existing
 * backend separates "uploaded asset" (File / ExternalLink rows) from
 * "task linkage" (TaskAttachment join rows) so the same asset can
 * be reused across multiple tasks. The frontend collapses that
 * separation into one click — the user never sees the join
 * complexity.
 */
export function TaskAttachmentsCell({
  taskId,
  spaceId,
  autoFocusAttachmentId,
  // Exact attachment count from the task list row. When provided, the closed
  // cell shows this number WITHOUT fetching — only the popover-open (or a
  // deep-link auto-open) triggers the full attachments GET. Omit it and the
  // cell falls back to the old fetch-on-mount behaviour.
  initialCount = null,
}) {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef(null)
  const popoverRef = useRef(null)
  // Deep-link focus: only the task-detail dialog passes
  // `autoFocusAttachmentId` (from a `?focusAttachment=<id>` notification link),
  // never the list-row cells — so the popover auto-opens for exactly one cell.
  // Once open, the shared focus-flash mechanism scrolls to + flashes the row
  // (tagged with `data-focus-id`). Fires once per matching id.
  const autoOpenedRef = useRef('')

  // Attachments query — fetched only when the popover is OPEN (or on a deep-link
  // auto-open), so the list view no longer fires one GET per row on load. The
  // count comes from `initialCount` (the list row's `attachmentCount`) until
  // then. If no count is supplied, fall back to the old fetch-on-mount so any
  // caller that doesn't pass it still shows a number.
  const attachmentsQuery = useQuery({
    queryKey: ['task-attachments', taskId],
    queryFn: async () => {
      const res = await api.get(`/tasks/${taskId}/attachments`)
      return res.data
    },
    enabled: Boolean(taskId) && (open || initialCount == null),
    staleTime: 30_000,
  })
  const attachments = attachmentsQuery.data?.data ?? []
  const count =
    attachmentsQuery.data?.data?.length ??
    (typeof initialCount === 'number' ? initialCount : 0)

  // Auto-open the popover when a notification deep-links to one of THIS task's
  // attachments, so the shared focus-flash can then reveal it. Once only.
  useEffect(() => {
    if (!autoFocusAttachmentId) return
    if (autoOpenedRef.current === autoFocusAttachmentId) return
    if (
      attachments.some((a) => String(a.id) === String(autoFocusAttachmentId))
    ) {
      autoOpenedRef.current = autoFocusAttachmentId
      setOpen(true)
    }
  }, [autoFocusAttachmentId, attachments])

  // Close the popover on outside click / Escape so it behaves like a
  // proper menu instead of trapping the user.
  useEffect(() => {
    if (!open) return undefined
    function onClick(e) {
      if (popoverRef.current?.contains(e.target)) return
      if (buttonRef.current?.contains(e.target)) return
      // The ConfirmDialog (and any other Modal we might mount inside
      // the popover) renders into its own portal, so it isn't part of
      // `popoverRef`'s subtree. Without this guard, clicking the
      // "Delete" button inside the confirm dialog reads as an
      // outside-click, the popover unmounts, the mutation in flight
      // gets cancelled — and the user sees nothing happen. Treat a
      // click anywhere inside any `role="dialog"` element as "still
      // inside our flow" and skip the close.
      if (e.target?.closest?.('[role="dialog"]')) return
      setOpen(false)
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Position the popover under the button. Uses a portal so the
  // popover escapes the row's `overflow: hidden` clipping and isn't
  // shoved off-screen when the cell sits near the right edge.
  // `anchorTop` / `anchorBottom` are the trigger button's viewport
  // edges. The popover measures its own height once mounted (see
  // AttachmentsPopover) and uses these to drop down, flip up, or clamp
  // — so it's never clipped off the bottom when the cell is on the last
  // visible row.
  const [pos, setPos] = useState({
    top: 0,
    left: 0,
    anchorTop: 0,
    anchorBottom: 0,
  })
  useEffect(() => {
    if (!open) return undefined
    // Keep the popover glued to the trigger while it's open. It's
    // `position: fixed` (portaled to <body>), so when the list scrolls —
    // often inside an inner scroll container — its coordinates go stale and it
    // drifts away from its row. Recompute on scroll (capture: true catches
    // scroll on ANY ancestor scroll container, not just window) and on resize.
    // Mirrors the TaskComments popover.
    function updatePos() {
      const r = buttonRef.current?.getBoundingClientRect()
      if (!r) return
      const w = 360
      // Default: pop down and align the popover's right edge to the
      // button's right edge so it stays in-viewport on the right side
      // of the page. Clamps to the viewport's left edge if the result
      // would underflow.
      let left = r.right - w
      if (left < 12) left = 12
      setPos({ top: r.bottom + 6, left, anchorTop: r.top, anchorBottom: r.bottom })
    }
    updatePos()
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open])

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        title={count > 0 ? `${count} attachment${count === 1 ? '' : 's'}` : 'Add file or link'}
        className={
          'inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[13px] cursor-pointer transition-colors ' +
          (count > 0
            ? 'text-[color:var(--color-accent-strong)] hover:bg-[color:var(--color-accent-bg)]'
            : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)]')
        }
      >
        <Paperclip size={14} />
        <span className="tabular-nums">
          {count > 0 ? count : 'Add'}
        </span>
      </button>
      {open
        ? createPortal(
            <AttachmentsPopover
              ref={popoverRef}
              top={pos.top}
              left={pos.left}
              anchorTop={pos.anchorTop}
              anchorBottom={pos.anchorBottom}
              taskId={taskId}
              spaceId={spaceId}
              attachments={attachments}
              loading={attachmentsQuery.isLoading}
              onClose={() => setOpen(false)}
            />,
            document.body,
          )
        : null}
    </>
  )
}

// ────────────────────────────────────────────────────────────────────────
// Popover body — list + Upload + Add link form.
// ────────────────────────────────────────────────────────────────────────

/** Link title must be real text — 2–120 chars containing at least one LETTER
 *  (so numbers-only or symbols-only titles are rejected) and no emoji. */
function validateLinkTitle(value) {
  const t = value.trim()
  if (t.length < 2) return 'Title must be at least 2 characters.'
  if (t.length > 120) return 'Title must be 120 characters or fewer.'
  if (CONTAINS_EMOJI.test(t)) return 'Title cannot contain emojis.'
  if (!/\p{L}/u.test(t)) return 'Title must contain at least one letter.'
  return null
}

/** Prepend https:// when the user omits a scheme, so "example.com" is
 *  accepted and stored as a proper URL. */
function normalizeLinkUrl(value) {
  const t = value.trim()
  if (!t) return ''
  return /^https?:\/\//i.test(t) ? t : `https://${t}`
}

/** URL must parse to a real http(s) address with a dotted hostname —
 *  rejects junk like "432#%" (host "432") while accepting "example.com"
 *  and "https://sub.example.com/path". */
function validateLinkUrl(value) {
  const t = value.trim()
  if (!t) return 'URL is required.'
  let parsed
  try {
    parsed = new URL(normalizeLinkUrl(t))
  } catch {
    return 'Please enter a valid URL (e.g. https://example.com).'
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return 'URL must start with http:// or https://.'
  }
  const host = parsed.hostname
  if (!host.includes('.') || host.startsWith('.') || host.endsWith('.')) {
    return 'Please enter a valid URL (e.g. https://example.com).'
  }
  // Reject all-numeric hosts (e.g. "12.34") that aren't a real IPv4 address —
  // require an alphabetic TLD, or a valid IPv4 / localhost.
  const isIpv4 = /^(\d{1,3}\.){3}\d{1,3}$/.test(host)
  const hasAlphaTld = /\.[a-zA-Z]{2,}$/.test(host)
  if (host !== 'localhost' && !isIpv4 && !hasAlphaTld) {
    return 'Please enter a valid URL (e.g. https://example.com).'
  }
  return null
}

const AttachmentsPopover = ({
  top,
  left,
  anchorTop,
  anchorBottom,
  taskId,
  spaceId,
  attachments,
  loading,
  onClose,
  ref,
}) => {
  const qc = useQueryClient()
  // Per-instance ids for the Add-link fields. `useId()` rather than a literal
  // because several of these popovers can be mounted at once (one per task
  // row) and duplicate DOM ids would cross-wire the labels.
  const uid = useId()
  const titleId = `${uid}-title`
  const urlId = `${uid}-url`
  const titleErrorId = `${uid}-title-error`
  const urlErrorId = `${uid}-url-error`
  // Read-only members (Space access = 'view' or 'comment') can SEE the
  // attached files/links but cannot upload, add links, or remove them.
  // `canModify` is true only for edit/full (fails open while loading /
  // when the API doesn't report a level — the backend still enforces).
  const { canModify } = useSpaceAccess(spaceId)
  // Real operator-configured per-file cap (Master Admin → Settings →
  // uploads.max_size_mb), not the 2 GB ceiling — so an over-cap file is
  // rejected instantly with a clear message instead of failing at the server.
  const maxUploadBytes = useMaxUploadBytes()
  const [mode, setMode] = useState('list') // 'list' | 'add-link' | 'upload'
  const fileInputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [linkTitle, setLinkTitle] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  // Public / Private for the item being added. A PRIVATE attachment is hidden
  // from other task collaborators (not just the library) — see the backend
  // task-attachments.service.listForTask. Default Public, as before.
  const [linkVisibility, setLinkVisibility] = useState('public')
  const [fileVisibility, setFileVisibility] = useState('public')
  // A file chosen in the OS picker, held so the user can choose a visibility
  // before it uploads (the old flow uploaded immediately as Public).
  const [pendingFile, setPendingFile] = useState(null)
  // `confirmTarget` drives the delete-confirmation modal. Null = no
  // pending delete. Holding the attachment row (not just the id) lets
  // the modal show the file name / link title in the prompt, which is
  // the difference between "Delete this?" and "Delete website.pdf?".
  const [confirmTarget, setConfirmTarget] = useState(null)

  // Keep the popover fully on-screen. After it mounts — and whenever its
  // height changes (list ↔ add-link, attachments finishing loading) — we
  // measure it and either drop it below the trigger, flip it above, or
  // clamp it within the viewport. This stops the fields/actions being
  // clipped off the bottom when the cell is on the last visible row.
  // `useLayoutEffect` runs before paint, so the provisional `top` from
  // the parent never flashes at the wrong spot.
  const [resolvedTop, setResolvedTop] = useState(top)
  useLayoutEffect(() => {
    const el = ref?.current
    if (!el) return
    const h = el.offsetHeight
    const vh = window.innerHeight
    const margin = 12
    const spaceBelow = vh - anchorBottom
    let next
    if (h + 6 <= spaceBelow) {
      next = anchorBottom + 6 // room below — drop down (default)
    } else if (anchorTop - 6 >= h) {
      next = anchorTop - h - 6 // not enough below, room above — flip up
    } else {
      next = Math.max(margin, vh - h - margin) // tight — clamp into view
    }
    setResolvedTop(next)
  }, [ref, top, anchorTop, anchorBottom, mode, loading, attachments.length, pendingFile])

  const detachMutation = useMutation({
    // Pass the whole attachment row so onSuccess can tailor the toast
    // copy ("File deleted successfully." vs "Links deleted
    // successfully.") to what was actually removed. The DELETE
    // endpoint only needs the id, so the second arg is unused on the
    // wire — it's just passed through to onSuccess via `variables`.
    mutationFn: async (attachment) =>
      api.delete(`/tasks/${taskId}/attachments/${attachment.id}`),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['task-attachments', taskId] })
      // The backend cascade-deletes the underlying File / ExternalLink
      // when this was the last attachment referencing it (see
      // task-attachments.service.detach). Invalidate every cache that
      // could be showing the now-deleted asset so the global Files &
      // Links page and any open Space's library refresh immediately.
      void qc.invalidateQueries({ queryKey: ['files'] })
      void qc.invalidateQueries({ queryKey: ['external-links'] })
      void qc.invalidateQueries({ queryKey: ['files', 'storage'] })
      // Space Overview "Files & Links" KPI counts File + ExternalLink rows
      // in the space, so a detach must refresh it locally too (realtime
      // covers OTHER tabs via RESOURCE_KEYS['task-attachment']).
      void qc.invalidateQueries({ queryKey: ['space-overview'] })
      setConfirmTarget(null)
      toast.success(
        variables?.kind === 'link'
          ? 'Links deleted successfully.'
          : 'File deleted successfully.',
      )
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  /** Upload flow — create File row, then attach. Wrapped in a single
   *  async function so failures at either step surface a single
   *  toast and the user doesn't end up with an orphaned File. */
  // Validate a freshly-picked file, then hold it so the user can choose a
  // visibility before the upload fires (mirrors the Files & Links page dialog).
  function onFilePicked(file) {
    if (!file) return
    // Reject unsupported types up front — the SAME allowlist the Files &
    // Links page uses (and the server enforces), so what you can attach in
    // a Space matches what the Files & Links page accepts. Instant friendly
    // message instead of a failed upload round-trip.
    if (!isAllowedUploadFile(file)) {
      toast.error(
        `This file type is not supported. Allowed extensions: ${UPLOAD_EXTENSIONS_TEXT}.`,
      )
      return
    }
    // Fail-fast pre-check against the REAL operator-configured cap so an
    // over-cap file is rejected instantly with a clear message instead of
    // streaming until the server aborts it. The server still enforces the
    // cap authoritatively.
    if (file.size > maxUploadBytes) {
      toast.error(
        `File is too large. Maximum size is ${Math.floor(maxUploadBytes / 1024 / 1024)} MB.`,
      )
      return
    }
    // Duplicate guard — a file of the same name is already attached. The server
    // would otherwise silently merge it into a new version, giving the user no
    // feedback; surface a clear message instead (defect).
    const incomingName = String(file.name ?? '').trim().toLowerCase()
    if (
      incomingName &&
      attachments.some(
        (a) =>
          a.kind === 'file' &&
          String(a.name ?? '').trim().toLowerCase() === incomingName,
      )
    ) {
      toast.error(`“${file.name}” is already attached to this task.`)
      return
    }
    setFileVisibility('public')
    setPendingFile(file)
    setMode('upload')
  }

  async function uploadAndAttach(file, visibility = 'public') {
    if (!file) return
    setBusy(true)
    try {
      const form = new FormData()
      form.append('file', file)
      // Pin to the Space — the backend skips the folder-required
      // gate when spaceId is set so a task attachment never demands
      // the user pick a tenant-wide folder.
      if (spaceId) form.append('spaceId', spaceId)
      form.append('visibility', visibility)
      const fileRes = await api.post('/files', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Override the shared 15s axios timeout — a large attachment
        // (up to the 100 MB cap) streams longer than that, and the
        // abort otherwise surfaced as a misleading "Can't reach the
        // server" error on a healthy connection. 10-minute ceiling.
        timeout: 10 * 60 * 1000,
      })
      const fileId = fileRes.data?.id
      if (!fileId) throw new Error('Upload did not return a file id.')
      try {
        await api.post(`/tasks/${taskId}/attachments`, { fileId })
      } catch (attachErr) {
        // Attach was rejected (e.g. comment/view access can't manage
        // attachments). Roll back the just-uploaded file so it doesn't
        // linger in Files & Links — the user should only see the toast,
        // not a half-completed upload.
        await api.delete(`/files/${fileId}`).catch(() => {})
        throw attachErr
      }
      toast.success('File added successfully.')
      setPendingFile(null)
      setMode('list')
      void qc.invalidateQueries({ queryKey: ['task-attachments', taskId] })
      // Broad `['files']` prefix matches both the global /files-links
      // page key AND any per-Space scoped key — the previous narrower
      // `['files', 'space', spaceId]` invalidation missed /files-links,
      // so newly uploaded files only showed up there after a manual
      // refresh.
      void qc.invalidateQueries({ queryKey: ['files'] })
      void qc.invalidateQueries({ queryKey: ['files', 'storage'] })
      // Keep the Space Overview "Files & Links" KPI in sync (see detach).
      void qc.invalidateQueries({ queryKey: ['space-overview'] })
    } catch (err) {
      // Plan-cap hit (storage limit on the /files upload): the global API
      // interceptor already shows the single amber "upgrade your plan"
      // warning, so suppress our own error toast.
      if (!isPlanLimitError(err)) toast.error(extractApiError(err).message)
    } finally {
      setBusy(false)
    }
  }

  async function addLink() {
    if (validateLinkTitle(linkTitle) || validateLinkUrl(linkUrl)) return
    setBusy(true)
    try {
      const linkRes = await api.post('/external-links', {
        title: linkTitle.trim(),
        url: normalizeLinkUrl(linkUrl),
        visibility: linkVisibility,
        ...(spaceId ? { spaceId } : {}),
      })
      const externalLinkId = linkRes.data?.id
      if (!externalLinkId) throw new Error('Link creation did not return an id.')
      try {
        await api.post(`/tasks/${taskId}/attachments`, { externalLinkId })
      } catch (attachErr) {
        // Attach rejected — roll back the just-created link so a blocked
        // comment/view user is left with only the toast, no orphan link.
        await api.delete(`/external-links/${externalLinkId}`).catch(() => {})
        throw attachErr
      }
      toast.success('Links added successfully.')
      setLinkTitle('')
      setLinkUrl('')
      setLinkVisibility('public')
      setMode('list')
      void qc.invalidateQueries({ queryKey: ['task-attachments', taskId] })
      // Invalidate the BROAD `['external-links']` prefix, not the
      // narrower `['external-links', 'space', spaceId]` shape — the
      // global Files & Links page reads from `['external-links']`
      // (no space scoping), and the detach handler already uses the
      // broad prefix. Without this match the newly-added link
      // wouldn't show up on /files-links until manual refetch.
      void qc.invalidateQueries({ queryKey: ['external-links'] })
      // Keep the Space Overview "Files & Links" KPI in sync (see detach).
      void qc.invalidateQueries({ queryKey: ['space-overview'] })
    } catch (err) {
      toast.error(extractApiError(err).message)
    } finally {
      setBusy(false)
    }
  }

  // Live validation for the Add-link form.
  const titleErr = validateLinkTitle(linkTitle)
  const urlErr = validateLinkUrl(linkUrl)

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Task attachments"
      // Fixed-position so it floats above row clipping. The width is
      // intentionally narrow (360 px) so it doesn't look like a full
      // dialog — it's a context menu for the cell.
      style={{ position: 'fixed', top: resolvedTop, left, width: 360, zIndex: 70 }}
      className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-xl"
    >
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[color:var(--color-rule)]">
        <h3 className="font-display text-sm font-semibold text-[color:var(--color-ink-strong)] m-0">
          {mode === 'add-link'
            ? 'Add link'
            : mode === 'upload'
              ? 'Upload file'
              : 'Files & Links'}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          title="Close"
          className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          <X size={14} />
        </button>
      </header>

      {mode === 'add-link' ? (
        <div className="p-4 space-y-3">
          {/* Unwrapped from a wrapping <label>: a label that WRAPS its control
           *  associates implicitly, so the ENTIRE label box forwarded clicks to
           *  the input — the empty space beside the caption, the gap above the
           *  input, and the validation <p> below it. QA hit exactly that:
           *  clicking above the textbox, not inside it, still started accepting
           *  input. Same shape as ui/Field.jsx FieldLabel — explicit `htmlFor`
           *  plus `block w-fit` keeps the click-the-label-to-focus affordance
           *  and the screen-reader association, while the hit target shrinks to
           *  the text the user can actually see. */}
          <div>
            <label
              htmlFor={titleId}
              className="block w-fit text-[12px] font-medium text-[color:var(--color-ink-strong)] mb-1"
            >
              Title
              <span className="text-[color:var(--color-priority-urgent)]"> *</span>
            </label>
            <input
              id={titleId}
              aria-describedby={linkTitle.trim() && titleErr ? titleErrorId : undefined}
              type="text"
              value={linkTitle}
              onChange={(e) => setLinkTitle(e.target.value)}
              placeholder="Enter a Title"
              minLength={2}
              maxLength={120}
              autoFocus
              className={
                'w-full rounded-lg border bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink-strong)] focus:outline-none ' +
                (linkTitle.trim() && titleErr
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent-border)]')
              }
            />
            {linkTitle.trim() && titleErr ? (
              <p id={titleErrorId} className="mt-1 text-[11px] text-[color:var(--color-priority-urgent)]">
                {titleErr}
              </p>
            ) : null}
          </div>
          <div>
            <label
              htmlFor={urlId}
              className="block w-fit text-[12px] font-medium text-[color:var(--color-ink-strong)] mb-1"
            >
              URL
              <span className="text-[color:var(--color-priority-urgent)]"> *</span>
            </label>
            <input
              id={urlId}
              aria-describedby={linkUrl.trim() && urlErr ? urlErrorId : undefined}
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className={
                'w-full rounded-lg border bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink-strong)] focus:outline-none ' +
                (linkUrl.trim() && urlErr
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent-border)]')
              }
            />
            {linkUrl.trim() && urlErr ? (
              <p id={urlErrorId} className="mt-1 text-[11px] text-[color:var(--color-priority-urgent)]">
                {urlErr}
              </p>
            ) : null}
          </div>
          <VisibilityPills
            value={linkVisibility}
            onChange={setLinkVisibility}
            kind="link"
            privateNote="Only you can see this link — hidden from other people on this task."
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setMode('list')
                setLinkVisibility('public')
              }}
              className="px-3 py-1.5 rounded-md text-sm text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={addLink}
              disabled={busy || Boolean(titleErr) || Boolean(urlErr)}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-[color:var(--color-accent-strong)] text-white hover:opacity-90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Saving…' : 'Save Link'}
            </button>
          </div>
        </div>
      ) : mode === 'upload' ? (
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm text-[color:var(--color-ink-strong)]">
            <UploadIcon size={14} className="text-[color:var(--color-accent-strong)] shrink-0" />
            <span className="font-medium truncate" title={pendingFile?.name}>
              {pendingFile?.name}
            </span>
          </div>
          <VisibilityPills
            value={fileVisibility}
            onChange={setFileVisibility}
            kind="file"
            privateNote="Only you and your Company Admin or Sub Admin can see this file — hidden from other people on this task."
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setPendingFile(null)
                setMode('list')
              }}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-sm text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => uploadAndAttach(pendingFile, fileVisibility)}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-[color:var(--color-accent-strong)] text-white hover:opacity-90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Action row — Upload + Add link buttons, side by side at
           *  the top of the popover so the user can act before
           *  scrolling through existing attachments. Hidden entirely
           *  for read-only members (view / comment access) — they get a
           *  quiet note instead so the popover reads as view-only. */}
          {canModify ? (
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[color:var(--color-rule)]">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UploadIcon size={14} />
              Upload
            </button>
            <button
              type="button"
              onClick={() => setMode('add-link')}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={14} />
              Add link
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={UPLOAD_ACCEPT_ATTR}
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) onFilePicked(f)
                // Reset so the same file can be picked again later.
                if (e.target) e.target.value = ''
              }}
            />
          </div>
          ) : (
            <p className="px-4 py-2.5 text-xs italic text-[color:var(--color-ink-muted)] border-b border-[color:var(--color-rule)] m-0">
              You can open files and links here, but adding or removing them
              needs Edit access.
            </p>
          )}

          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              <p className="px-4 py-6 text-sm italic text-[color:var(--color-ink-muted)] text-center m-0">
                Loading attachments…
              </p>
            ) : attachments.length === 0 ? (
              <p className="px-4 py-6 text-sm italic text-[color:var(--color-ink-muted)] text-center m-0">
                {canModify
                  ? 'No files or links yet. Use the buttons above to add some.'
                  : 'No files or links on this task.'}
              </p>
            ) : (
              <ul className="divide-y divide-[color:var(--color-rule)]">
                {attachments.map((a) => (
                  <AttachmentRow
                    key={a.id}
                    attachment={a}
                    // Read-only members can't detach — hide the remove
                    // control entirely (the backend also rejects it).
                    onDelete={canModify ? () => setConfirmTarget(a) : null}
                  />
                ))}
              </ul>
            )}
          </div>
        </>
      )}
      {/* Confirm-before-delete modal — covers both files and links.
       *  Stops accidental clicks on the trash icon from destroying
       *  an attachment (and, via the backend cascade, the underlying
       *  asset). Re-uses the shared ConfirmDialog component so the
       *  styling matches every other destructive flow in the app. */}
      <ConfirmDialog
        open={Boolean(confirmTarget)}
        // This confirm is opened from ON TOP of the Files & Links popup, whose
        // panel sits at zIndex 70. Modal's default overlay is z-50, so without
        // this the confirmation paints BEHIND the popup and can't be reached
        // (most visible on mobile, where the popup fills the width). Raise the
        // overlay above it — reuses the same `z-[90]` tier the comments-popover
        // confirm uses, so it clears both the popup (70) and any popover (80).
        overlayZClass="z-[90]"
        title={
          confirmTarget?.kind === 'link' ? 'Remove This Link?' : 'Delete This File?'
        }
        message={
          confirmTarget?.kind === 'link'
            ? `"${confirmTarget?.title ?? 'This link'}" will be removed from this task. If no other task references it, the link will be deleted from Files & Links too.`
            : `"${confirmTarget?.name ?? 'This file'}" will be removed from this task. If no other task references it, the file will be permanently deleted (this cannot be undone).`
        }
        confirmLabel={
          confirmTarget?.kind === 'link' ? 'Remove' : 'Delete'
        }
        variant="danger"
        confirming={detachMutation.isPending}
        onCancel={() =>
          detachMutation.isPending ? undefined : setConfirmTarget(null)
        }
        onConfirm={() => {
          if (confirmTarget?.id) detachMutation.mutate(confirmTarget)
        }}
      />
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────
// One attachment row inside the popover list.
// ────────────────────────────────────────────────────────────────────────

function iconForFile(mimeType) {
  const mt = String(mimeType ?? '').toLowerCase()
  if (mt.startsWith('image/')) return ImageIcon
  if (mt.includes('spreadsheet') || mt === 'text/csv') return FileSpreadsheet
  if (mt.includes('presentation')) return Presentation
  if (mt.startsWith('application/pdf') || mt.startsWith('text/'))
    return FileText
  return FileIcon
}

function AttachmentRow({ attachment, onDelete }) {
  if (attachment.kind === 'file') {
    async function download() {
      try {
        // `?download=1` makes the backend sign the URL with
        // Content-Disposition: attachment, so the browser SAVES the file
        // instead of previewing it — images / PDFs were otherwise opening in a
        // new tab. A programmatic anchor click (rather than window.open) then
        // downloads without flashing a blank tab.
        const res = await api.get(`/files/${attachment.fileId}/download`, {
          params: { download: 1 },
        })
        const url = res.data?.url
        if (url) {
          const a = document.createElement('a')
          a.href = url
          a.rel = 'noopener noreferrer'
          document.body.appendChild(a)
          a.click()
          a.remove()
        }
      } catch (err) {
        toast.error(extractApiError(err).message)
      }
    }
    return (
      <li
      data-focus-id={attachment.id}
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-[color:var(--color-card-hover)]"
    >
        <span className="h-7 w-7 rounded-md bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] flex items-center justify-center text-[color:var(--color-ink-muted)] shrink-0">
          {createElement(iconForFile(attachment.mimeType), { size: 13 })}
        </span>
        <p
          className="text-sm text-[color:var(--color-ink-strong)] truncate flex-1 m-0"
          title={attachment.name}
        >
          {attachment.name}
        </p>
        {attachment.visibility === 'private' ? (
          <Lock
            size={12}
            className="shrink-0 text-[color:var(--color-ink-muted)]"
            aria-label="Private"
            title="Private — hidden from other people on this task"
          />
        ) : null}
        <button
          type="button"
          onClick={download}
          aria-label="Download"
          title="Download"
          className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card)] cursor-pointer"
        >
          <Download size={13} />
        </button>
        {onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            aria-label="Delete"
            title="Delete"
            className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-danger)] hover:bg-[color:var(--color-card)] cursor-pointer"
          >
            <Trash2 size={13} />
          </button>
        ) : null}
      </li>
    )
  }
  // link attachment
  return (
    <li
      data-focus-id={attachment.id}
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-[color:var(--color-card-hover)]"
    >
      <span className="h-7 w-7 rounded-md bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] flex items-center justify-center text-[color:var(--color-ink-muted)] shrink-0">
        <Link2 size={13} />
      </span>
      <div className="min-w-0 flex-1">
        <p
          className="text-sm text-[color:var(--color-ink-strong)] truncate m-0 flex items-center gap-1.5"
          title={attachment.title}
        >
          <span className="truncate">{attachment.title}</span>
          {attachment.visibility === 'private' ? (
            <Lock
              size={11}
              className="shrink-0 text-[color:var(--color-ink-muted)]"
              aria-label="Private"
              title="Private — only you can see this link"
            />
          ) : null}
        </p>
        <p
          className="text-[11px] text-[color:var(--color-ink-muted)] truncate m-0"
          title={attachment.domain || attachment.url}
        >
          {attachment.domain || attachment.url}
        </p>
      </div>
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open link"
        title="Open link"
        className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card)] cursor-pointer"
      >
        <ExternalLinkIcon size={13} />
      </a>
      {onDelete ? (
        <button
          type="button"
          onClick={onDelete}
          aria-label="Remove"
          title="Remove"
          className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-danger)] hover:bg-[color:var(--color-card)] cursor-pointer"
        >
          <Trash2 size={13} />
        </button>
      ) : null}
    </li>
  )
}
